import {
  AVACUS_RANK,
  AVACUS_RANKING_LEVELS,
  AVACUS_RANK_ICON,
  AuthQueryKeys,
  CookieKey,
} from "@/configs/common"
import { useConnection } from "wagmi"
import { useCookies } from "react-cookie"

import { CustomUseQueryOptions } from "@/types/common"
import { getAvacusProfileApi } from "@/services/auth"
import { jwtDecode } from "jwt-decode"
import { shortenAddressWithCenterDots } from "@/lib/utils"
import { useAppKitAccount } from "@reown/appkit/react"
import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

interface UseGetAvacusProfileProps extends CustomUseQueryOptions {
  address?: `0x${string}`
}

export const useGetAvacusProfile = ({
  address,
  ...props
}: UseGetAvacusProfileProps = {}) => {
  const { address: connectedAddress } = useConnection()
  const { address: appKitAddress } = useAppKitAccount()
  const [cookies] = useCookies([CookieKey.AuthToken])
  const accessToken = cookies[CookieKey.AuthToken] as string | undefined

  const getAvacusProfile = async () => {
    const targetAddress = address || connectedAddress || appKitAddress
    if (!targetAddress) return null
    return await getAvacusProfileApi(targetAddress)
  }

  const queryResult = useQuery({
    queryKey: [AuthQueryKeys.AVACUS_PROFILE, address || connectedAddress],
    queryFn: getAvacusProfile,
    enabled: !!(address || connectedAddress),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Retry on 404 (user might not exist yet, retry a few times)
      // Retry on network errors
      // Don't retry on other errors
      if (error?.response?.status === 404) {
        return failureCount < 3 // Retry up to 3 times for 404
      }
      if (error?.response?.status >= 500) {
        return failureCount < 2 // Retry up to 2 times for server errors
      }
      return false // Don't retry for other errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff
    ...props,
  })

  // Calculate derived data
  const user = queryResult.data
  const computedAddress = useMemo(() => {
    const addressFromAccessToken = accessToken
      ? jwtDecode<{ wallet_address?: string }>(accessToken)?.wallet_address
      : undefined
    return (
      user?.wallet_address ??
      (address || connectedAddress || addressFromAccessToken) ??
      undefined
    )
  }, [accessToken, user?.wallet_address, address, connectedAddress])

  const displayName = useMemo(
    () =>
      user?.display_name ??
      shortenAddressWithCenterDots(computedAddress ?? "", 4, 6) ??
      undefined,
    [user?.display_name, computedAddress]
  )

  const rank = useMemo(() => {
    const score = user?.percentile_score

    // Early return for invalid scores
    if (score == null) {
      return {
        level: AVACUS_RANK.NORMAL,
        icon: AVACUS_RANK_ICON[AVACUS_RANK.NORMAL],
      }
    }

    // Define rank thresholds in descending order
    const rankThresholds = [
      { level: AVACUS_RANK.GOLD, threshold: AVACUS_RANKING_LEVELS.gold },
      { level: AVACUS_RANK.SILVER, threshold: AVACUS_RANKING_LEVELS.silver },
      { level: AVACUS_RANK.BRONZE, threshold: AVACUS_RANKING_LEVELS.bronze },
    ] as const

    // Find the highest rank the score qualifies for
    const matchedRank = rankThresholds.find(
      ({ threshold }) => score > threshold
    )

    return matchedRank
      ? {
          level: matchedRank.level,
          icon: AVACUS_RANK_ICON[matchedRank.level],
        }
      : {
          level: AVACUS_RANK.NORMAL,
          icon: AVACUS_RANK_ICON[AVACUS_RANK.NORMAL],
        }
  }, [user])

  return {
    ...queryResult,
    address: computedAddress,
    displayName,
    rank,
  }
}
